// Desktop/tablet filter bar for /assets — one bordered container: primary
// row (search + lifecycle + condition + manufacturer + "More filters" +
// missing-financials toggle), an inline disclosure panel for secondary
// controls, a chip row, and a meta row. Sort lives outside this component
// entirely (index.tsx renders it on the results header — sort is a view
// concern, not a filter, and does not narrow the result set).
//
// Deviation from the spec's literal primary list ("Search, Asset class,
// Lifecycle, Condition"): this route has no asset-class FILTER (class only
// drives grouping) — see plans/filter-standardization-spec.md §1's
// correction note. Manufacturer fills the 4th primary slot instead.

import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FilterChip } from '@siliconx/shared/ui/FilterChip';
import {
  SearchField,
  LifecycleField,
  ConditionField,
  ManufacturerField,
  ModelField,
  LocationField,
  LotField,
  TeamField,
  MissingFinancialsToggle,
} from './FilterFields';
import { hasSecondaryActive, type FilterBarProps } from './filterTypes';
import { OrgFeature } from '../../lib/orgFeatures';

export function FilterBar(props: FilterBarProps) {
  // Auto-expands on load if a secondary filter is already active (e.g. a
  // bookmarked ?data_center_id=... link) — derived, not persisted.
  const [expanded, setExpanded] = useState(() => hasSecondaryActive(props));
  const wasExpanded = useRef(expanded);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const secondaryActiveCount = [
    props.modelDraft,
    props.locationFilter,
    props.lotFilter,
    props.teamFilter,
  ].filter(Boolean).length;

  // Re-derive the auto-expand whenever a secondary filter arrives via URL
  // (e.g. browser back/forward, or a chip removal that leaves another
  // secondary filter active) without re-collapsing a panel the user opened
  // by hand.
  useEffect(() => {
    if (hasSecondaryActive(props) && !expanded) setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to secondary-filter presence, not every prop
  }, [props.modelDraft, props.locationFilter, props.lotFilter, props.teamFilter]);

  // Disclosure focus management (WAI-ARIA Disclosure pattern): on expand,
  // move focus to the panel's first control; on collapse (explicit click,
  // not blur), return focus to the trigger.
  useEffect(() => {
    if (expanded && !wasExpanded.current) {
      const firstField = panelRef.current?.querySelector<HTMLElement>('input, select');
      firstField?.focus();
    } else if (!expanded && wasExpanded.current) {
      triggerRef.current?.focus();
    }
    wasExpanded.current = expanded;
  }, [expanded]);

  return (
    <div className="hidden rounded-md border border-border bg-card p-3 sm:block">
      <div className="flex flex-wrap items-end gap-3">
        <SearchField {...props} className="min-w-[14rem] max-w-sm flex-1" />
        <LifecycleField {...props} />
        <ConditionField {...props} />
        <ManufacturerField {...props} />
        <button
          ref={triggerRef}
          type="button"
          onClick={() => { setExpanded((v) => !v); }}
          aria-expanded={expanded}
          aria-controls="assets-filter-bar-more"
          className="inline-flex h-11 items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground hover:bg-muted"
        >
          More filters{secondaryActiveCount > 0 ? ` (${secondaryActiveCount})` : ''}
          <ChevronDown className={`size-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        <div className="ml-auto">
          {/* SIL-1035: an individual customer_org has no financials/ERP
              register — 'finance' gates financials/*, erp-register/*. */}
          <OrgFeature name="finance">
            <MissingFinancialsToggle {...props} />
          </OrgFeature>
        </div>
      </div>

      {expanded && (
        <div ref={panelRef} id="assets-filter-bar-more" className="mt-3 flex flex-wrap items-end gap-3 border-t border-border pt-3">
          <ModelField {...props} />
          {/* SIL-1035: an individual customer_org has no seller-facility
              concept — GET /api/client/locations is orgFeature: 'locations'-
              gated and would 403. */}
          <OrgFeature name="locations">
            <LocationField {...props} />
          </OrgFeature>
          <OrgFeature name="lots">
            <LotField {...props} />
          </OrgFeature>
          {props.isAdmin && (
            <OrgFeature name="teams">
              <TeamField {...props} />
            </OrgFeature>
          )}
        </div>
      )}

      <ChipRow {...props} />

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2">
        <ResultCountMeta {...props} />
      </div>
    </div>
  );
}

/** Result count + active-filter-count meta text. Always visible once data
 *  has loaded — connective tissue between "I changed a filter" and "here's
 *  what changed." `isFetching` surfaces an in-flight cue (debounced text +
 *  select changes both go through this), not just a silent re-render. */
export function ResultCountMeta({ resultCount, activeChips, isFetching }: FilterBarProps) {
  return (
    <span aria-live="polite" className="text-xs text-muted-foreground">
      {resultCount} {resultCount === 1 ? 'asset' : 'assets'}
      {activeChips.length > 0 && ` · ${activeChips.length} filter${activeChips.length === 1 ? '' : 's'} active`}
      {isFetching && <span className="ml-1 opacity-70">· Updating…</span>}
    </span>
  );
}

/** Individually-removable active-filter chip row + Clear all. */
export function ChipRow({ activeChips, onClearAll }: FilterBarProps) {
  if (activeChips.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
      {activeChips.map((chip) => (
        <FilterChip key={chip.key} label={chip.label} value={chip.value} onRemove={chip.onRemove} />
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="tap-target-44 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        Clear all
      </button>
    </div>
  );
}
